import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, ViewEncapsulation, Input, HostBinding, NgModule } from '@angular/core';
import { BaseDirective } from '@eui/components/shared';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';

class EuiBlockContentComponent extends BaseDirective {
    constructor(elRef, renderer) {
        super();
        this.elRef = elRef;
        this.renderer = renderer;
        this._isBlocked = false;
        /** keeps the state of the element that was focused before content was blocked */
        this.lastActiveElement = null;
    }
    get isBlocked() { return this._isBlocked; }
    set isBlocked(value) { this._isBlocked = coerceBooleanProperty(value); }
    get cssClasses() {
        return [
            this.isBlocked ? `eui-block-content--blocked` : ``,
            super.getCssClasses('eui-block-content'),
        ].join(' ').trim();
    }
    ngOnChanges(changes) {
        if (changes['isBlocked']) {
            const activeElement = this.getActiveElement();
            const isBlocking = this.isAboutToBlock(changes);
            if (isBlocking) {
                this.deactivateElement(activeElement);
            }
            else {
                this.reactivateElement(this.lastActiveElement);
            }
        }
    }
    /**
     * blur (un-focus) the focused element inside BlockContent
     */
    deactivateElement(activeElement) {
        if (activeElement) {
            this.renderer.selectRootElement(activeElement, true).blur();
            this.lastActiveElement = activeElement;
        }
        else {
            this.lastActiveElement = null;
        }
    }
    /**
     * focus on element if it was focused before content was blocked
     */
    reactivateElement(element) {
        if (element) {
            this.renderer.selectRootElement(element, true).focus();
        }
    }
    /**
     * returns where the element is about to block or not
     * @param changes SimplyChanges
     */
    isAboutToBlock(changes) {
        const isBlockedChange = changes['isBlocked'];
        return isBlockedChange?.currentValue && !isBlockedChange.previousValue;
    }
    /**
     * finds the element which is focused inside the UxBlockContent
     */
    getActiveElement() {
        return this.elRef.nativeElement.querySelector(':focus');
    }
}
/** @nocollapse */ EuiBlockContentComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiBlockContentComponent, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiBlockContentComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiBlockContentComponent, selector: "eui-block-content", inputs: { isBlocked: "isBlocked" }, host: { properties: { "class": "this.cssClasses" } }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<div class=\"eui-block-content__wrapper\" aria-label=\"eUI Block Content wrapper\" role=\"region\">\n    <ng-content></ng-content>\n</div>\n", styles: [".eui-block-content--blocked .eui-block-content__wrapper{min-height:100px;position:relative;z-index:var(--eui-base-z-index-overlay)}.eui-block-content--blocked .eui-block-content__wrapper:before{animation:spin .8s linear infinite;border:8px solid transparent;border-radius:50%;border-top-color:var(--eui-base-color-primary-100);content:\"\";display:block;height:80px;left:50%;margin:-40px 0 0 -40px;position:absolute;top:50%;width:80px;z-index:10002}.eui-block-content--blocked .eui-block-content__wrapper:after{background-color:#fff6;content:\"\";height:100%;left:0;position:absolute;top:0;width:100%;z-index:10001}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiBlockContentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'eui-block-content', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<div class=\"eui-block-content__wrapper\" aria-label=\"eUI Block Content wrapper\" role=\"region\">\n    <ng-content></ng-content>\n</div>\n", styles: [".eui-block-content--blocked .eui-block-content__wrapper{min-height:100px;position:relative;z-index:var(--eui-base-z-index-overlay)}.eui-block-content--blocked .eui-block-content__wrapper:before{animation:spin .8s linear infinite;border:8px solid transparent;border-radius:50%;border-top-color:var(--eui-base-color-primary-100);content:\"\";display:block;height:80px;left:50%;margin:-40px 0 0 -40px;position:absolute;top:50%;width:80px;z-index:10002}.eui-block-content--blocked .eui-block-content__wrapper:after{background-color:#fff6;content:\"\";height:100%;left:0;position:absolute;top:0;width:100%;z-index:10001}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { isBlocked: [{
                type: Input
            }], cssClasses: [{
                type: HostBinding,
                args: ['class']
            }] } });

class EuiBlockContentModule {
}
/** @nocollapse */ EuiBlockContentModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiBlockContentModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiBlockContentModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiBlockContentModule, declarations: [EuiBlockContentComponent], imports: [CommonModule], exports: [EuiBlockContentComponent] });
/** @nocollapse */ EuiBlockContentModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiBlockContentModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiBlockContentModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [EuiBlockContentComponent],
                    declarations: [EuiBlockContentComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { EuiBlockContentComponent, EuiBlockContentModule };
